import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { selectCurrentUser, selectUser, selectUserObject } from "@common-reducers/UserSelectors";
import {
  PersonPickerIDType,
  PersonPickerValue,
} from "@common-components/tasks/columns-cells/person-picker-cell/person-picker-cell";
import { Board } from "@common-models/board";
import { BlueticksBoardUser } from "@common-models/board-user";
import { Gantt } from "@common-models/gantt";
import { Kanban } from "@common-models/kanban";
import { Person } from "@common-models/person";
import { User, UserInterface } from "@common-models/user";
import { BlueticksWorkspaceUser } from "@common-models/workspace-user";
import { createSelector } from "@reduxjs/toolkit";
import { createDeepEqualSelector } from "@common-services/deep-equal-selector";
import { isExtensionContextOrWhatsappView } from "@common-services/detect-context";
import { Chat } from "@bt-common/interfaces/whatsapp-types";
import moment from "moment";
import { NOT_DELETED_FILTER } from "@common/constants/database-filters";
import { sortBoardsByRecency } from "@common-reducers/board-recency";

import { blueticksDefultTheme, blueticksLeanTheme } from "@common/theme";
import { BoardsSelectPaginationStatus, BulkListSelectPaginationStatus, boardUserSelectByQuery, boardUserSelectOneObjectByQuery, boardViewSelectByQuery, boardsSelectByQuery, boardsSelectOneObjectById, boardsSelectOneObjectByQuery, boardsSelectors, formSelectByQuery, ganttSelectByQuery, isTempId, kanbanSelectByQuery, taskColumnOptionSelectByQuery, taskGroupsSelectOneFieldByQuery, tasksExtraDataSelectByQuery, tasksSelectOneFieldById, viewFiltersSelectByQueryOnlyLength, workloadSelectByQuery, workspaceUserSelectByQuery } from "@common-reducers/DBServiceThunks";
import { NullableId } from "@common/types/interface";
import { CommonRootState } from "@common/types/common-root-state-type";


export type AugmentedBlueticksBoardUser = Partial<BlueticksBoardUser> & {
  type: PersonPickerIDType;
};


export const getIsLoadingBoards = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => !state.DBBoardsReducer.itemsLoaded
);


export const getBoardByChat = createSelector(
  [
    (state: CommonRootState) => state.DBBoardsReducer,
    (state: CommonRootState) => state.BoardsReducer,
    (state: CommonRootState, chat: Chat) => chat,
    (state: CommonRootState) => state.TasksReducer.isTasksPanelOpen,
    (state: CommonRootState) => state.WhatsAppReducer.me,
    // Also check if Tasks sidebar is open in the inline sidebar
    (state: CommonRootState) => state.BtLayoutSidebarReducer?.isOpen && state.BtLayoutSidebarReducer?.componentToShow === 'Tasks',

  ],
  (DBBoardsReducer, BoardsReducer, chat: Chat, isTasksPanelOpen, userWA, isTasksSidebarOpen): Board | undefined => {
    let currentBoard: Board | undefined = undefined;

    // Check if Tasks panel/sidebar is open (either old panel or new inline sidebar)
    const isTasksOpen = isTasksPanelOpen || isTasksSidebarOpen;

    if (BoardsReducer?.selectedBoardId) {
       const selected = boardsSelectOneObjectById(
        DBBoardsReducer,
        BoardsReducer.selectedBoardId
      );
      // Only return the selected board if it belongs to the current chat
      // or if Tasks panel is not open (to preserve backward compatibility)
      if (selected) {
        if (!isTasksOpen) return selected;

        // If Tasks is open, verify the board belongs to current chat
        const chatId = chat?.id?._serialized;
        if (chatId && selected._IdByWaChatIds?.includes(chatId)) {
          return selected;
        }
        // If board doesn't match current chat, fall through to find the correct one
      }
    }

    if (isExtensionContextOrWhatsappView() && chat && isTasksOpen) {
      let _IdByWaChatIds: string[];

      if (chatKindOf(chat?.id?._serialized) === 'group') {
        _IdByWaChatIds = [chat?.id?._serialized].filter((id): id is string => id !== undefined);
      } else {
        // Filter out any missing values to avoid [$all: [chatId, undefined]] which never matches
        _IdByWaChatIds = [chat?.id?._serialized, userWA?.id?._serialized].filter((id): id is string => Boolean(id));
      }

      const res = boardsSelectByQuery(DBBoardsReducer, {
        _IdByWaChatIds: { $all: _IdByWaChatIds },
      });

      if (res.length >= 1) {
        currentBoard = res[0];
      }
      if (res.length > 1) {
        console.error("more then one board - check it");
      }
    }

    return currentBoard;
  }
);

export const getIsChatHasBoard = createSelector(
  [
    (state: CommonRootState) => state,
  ],
  (state: CommonRootState) => {
    let isChatHasBoard = false
    let currentBoard
    const currentChat = state.WhatsAppReducer.currentChat

    if (isExtensionContextOrWhatsappView() && currentChat) {
      let _IdByWaChatIds: string[];

      const userWA = state.WhatsAppReducer.me;

      if (chatKindOf(currentChat?.id?._serialized) === 'group') {
        _IdByWaChatIds = [currentChat?.id?._serialized].filter((id): id is string => id !== undefined);
      } else {
        // Filter out any missing values so we can match boards created earlier with null in the array
        _IdByWaChatIds = [currentChat?.id?._serialized, userWA?.id?._serialized].filter((id): id is string => Boolean(id));
      }

      const res = boardsSelectByQuery(state.DBBoardsReducer, {
        _IdByWaChatIds: { $all: _IdByWaChatIds },
      });

      if (res.length >= 1) {
        currentBoard = res[0];
        if (!isTempId(currentBoard._id ?? null)) {
          isChatHasBoard = true
        }
      }
    }

    return isChatHasBoard
  }
);

export const getCurrentBoard = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState): Board | undefined => {
    // NOTE: do NOT early-return when there's no currentChat. In the web
    // dashboard there is never a WhatsApp chat, and getBoardByChat resolves
    // the board from BoardsReducer.selectedBoardId in that case. Returning
    // undefined here breaks opening a board by URL (boardID) on the web.
    const chat = state.WhatsAppReducer?.currentChat;
    let currentBoard: Board | undefined = getBoardByChat(
      state,
      chat as Chat
    );
    return currentBoard;
  }
);

export const getCurrentBoardId = createSelector(
  (state: CommonRootState) => getCurrentBoard(state),
  (currentBoard) => currentBoard?._id
);

export const currentBoardExistsSelector = createSelector(
  [(state: CommonRootState) => getCurrentBoard(state)],
  (board: Board | undefined): boolean => {
    return !!board;
  }
);

export const getSelectedBoardPersonList = createSelector(
  [(state: CommonRootState) => getCurrentBoard(state)],
  (board: Board | undefined): Person[] => {
    return board?.personList ?? [];
  }
);

export const getSelectedBoardId = createSelector(
  [(state: CommonRootState) => getCurrentBoard(state)],
  (board: Board | undefined): string => {
    return board?._id ?? "";
  }
);

export const getMockPersonList = createSelector(
  [
    (state: CommonRootState) => state,
    getSelectedBoardId,
  ],
  (state: CommonRootState, boardId) => {
    const _mockPersonList = taskColumnOptionSelectByQuery(state.DBTaskColumnOptionReducer, {
      boardId,
      imageUrl: { $exists: true },
    })
    const mockPersonList = _mockPersonList.map(option => {
      const words = option.label?.split(' ') ?? [];
      const firstName = words[0];
      const lastName = words.slice(1).join(' ');

      const workspaceUser: AugmentedBlueticksBoardUser = {
        _id: option._id,
        status: 'active',
        profile: {
          firstName,
          lastName,
          profileImg: option.imageUrl,
        } as User,
        type: 'option'
      }
      return workspaceUser;
    })
    return mockPersonList
  }

);

export const getBlueticksThemeSelector = createSelector([
  (state: CommonRootState) => state],
  (state: CommonRootState) => {
    let theme
    if (state.BoardsReducer.boardTheme === 'lean') {
      theme = blueticksLeanTheme
    }
    else {
      theme = blueticksDefultTheme
    }
    return theme
  }
);

export const getSelectedBoardUserList = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState, boardId?: string) => {
      const currentBoard = boardId
        ? boardsSelectors.selectById(state, boardId)
        : getCurrentBoard(state);
      return currentBoard;
    },
  ],
  (state: CommonRootState, board: Board): BlueticksBoardUser[] => {
    const boardUserList = boardUserSelectByQuery(state.DBBoardUserReducer, {
      boardId: board?._id,
    });
    const workspaceUserList = workspaceUserSelectByQuery(
      state.DBWorkspaceUserReducer,
      {
        workspaceId: board?.workspaceId,
      }
    );

    // we are crossing the workspaceUserList with the boardUserList to get the workspaceUser role

    return boardUserList.map((boardUser) => ({
      ...boardUser,
      workspaceUser: workspaceUserList.find(
        (workspaceUser) =>
          (workspaceUser?.userId &&
            workspaceUser.userId === boardUser.userId) ||
          (workspaceUser?.email && workspaceUser.email === boardUser.email)
      ),
    }));
  }
);

export const getCurrentWorkspaceUserList = createSelector(
  [
    (state: CommonRootState) => state.DBWorkspaceUserReducer,
    (state: CommonRootState) => selectCurrentUser(state),
  ],
  (workspaceUserReducer, user): BlueticksWorkspaceUser[] => {
    if (!user?.currentWorkspace) {
      return [];
    }

    return workspaceUserSelectByQuery(workspaceUserReducer, {
      workspaceId: user?.currentWorkspace,
    });
  }
);


export const getBoardUsersProfile = createSelector(
  [
    (state: CommonRootState) => state,
  ],
  (state) => {
    // dispatch(DBUserThunks.find({ _id: user._id }))
    const boardUsers = boardUserSelectByQuery(state.DBBoardUserReducer,
      {
        ...NOT_DELETED_FILTER,
        status: 'active',
      },)
    const mentionUsersArray = boardUsers.map((user) => { return ({ ...user.profile, userId: user.userId }) })
    return (
      mentionUsersArray
    );
  }
);


export const getBoardUser = createSelector(
  [
    (state: CommonRootState) => state,
  ],
  (state) => {
    const userId = state?.UserReducer?.userId
    const [boardUser] = boardUserSelectByQuery(state.DBBoardUserReducer, { deleted: false })
      .filter(user => user?.userId === userId)
    return (
      boardUser?._id
    );
  }
);


export const getSeenByUsersProfile = createSelector(
  [
    (state: CommonRootState, seenByIdArray) => state,
    (_, seenByIdArray) => seenByIdArray,
  ],
  (state: CommonRootState, seenByIdArray) => {
    const boardUsers = boardUserSelectByQuery(state.DBBoardUserReducer, { deleted: false })

    const returnValue = boardUsers.filter((user) => seenByIdArray.includes(user.userId)).map((user) => user.profile)

    return returnValue
  }
);


export const getLikedByUsersProfile = createSelector(
  [
    (state: CommonRootState, likedByIdArray) => state,
    (_, likedByIdArray) => likedByIdArray,
  ],
  (state: CommonRootState, likedByIdArray) => {
    const boardUsers = boardUserSelectByQuery(state.DBBoardUserReducer, { deleted: false })

    const returnValue = boardUsers.filter((user) => likedByIdArray.includes(user.userId)).map((user) => user.profile)

    return returnValue
  }
);


export const getTaskExtradataArrayForMessageThread = createSelector(
  [
    (state: CommonRootState, parentItemId, displayExtraDataOfTaskById) => state,
    (state: CommonRootState, parentItemId, displayExtraDataOfTaskById) => parentItemId,
    (state: CommonRootState, parentItemId, displayExtraDataOfTaskById) => displayExtraDataOfTaskById,
  ],
  (state: CommonRootState, parentItemId, taskId) => {

    let taskExtradataForMessageThread;

    const taskExtradataArray = tasksExtraDataSelectByQuery(
      state.DBTasksExtraDataReducer,
      {
        taskId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      {
        isPinedToTop: -1,
        updatedAt: -1
      }
    );

    if (parentItemId !== undefined) {
      // If parentItemId is defined, sort from oldest to newest by updatedAt
      taskExtradataForMessageThread = taskExtradataArray
        .filter((extraData) => extraData.parentItemId === parentItemId)
        .sort((a, b) => moment(a.updatedAt).diff(moment(b.updatedAt)));
    } else {
      // If parentItemId is undefined, sort from newest to oldest by updatedAt
      taskExtradataForMessageThread = taskExtradataArray
        .filter((extraData) => !extraData.parentItemId)
        .sort((a, b) => moment(b.updatedAt).diff(moment(a.updatedAt)));
    }

    // Move pinned items to the top
    taskExtradataForMessageThread.sort((a, b) => {
      if (a.isPinedToTop && !b.isPinedToTop) return -1;
      if (!a.isPinedToTop && b.isPinedToTop) return 1;
      return 0;
    });

    return taskExtradataForMessageThread;
  }
);

export const getSortedAndFilteredWorkspaceUserList = createSelector(
  [getCurrentWorkspaceUserList, (state: CommonRootState) => state],
  (workspaceUserList, state) => {
    if (workspaceUserList.length === 0) {
      return [];
    }

    const searchFilter = state.WorkspaceReducer.workSpaceUsersFilter;
    const userTypeFilter = state.WorkspaceReducer.userTypeFilter;
    const userStatusFilter = state.WorkspaceReducer.userStatusFilter;
    const isAnyUserTypeSelected = Object.values(userTypeFilter).some(value => value);
    const isAnyUserStatusSelected = Object.values(userStatusFilter).some(value => value);

    return workspaceUserList
      .filter(user => {
        if (isTempId(user._id ?? null)) {
          return false;
        }

        // Check if user has a profile object
        const { firstName, lastName, email } = user?.profile || {};
        const searchFields = firstName || lastName ? [firstName, lastName, email] : [user?.email];
        const matchesSearch = searchFilter ? searchFields.some(field =>
          field?.toLowerCase().includes(searchFilter.toLowerCase())
        ) : true;

        const matchesUserType = isAnyUserTypeSelected && user?.role !== undefined ? userTypeFilter[user.role as keyof typeof userTypeFilter] : true;
        const matchesUserStatus = isAnyUserStatusSelected && user?.status !== undefined ? userStatusFilter[user.status as keyof typeof userStatusFilter] : true;

        return matchesSearch && matchesUserType && matchesUserStatus;
      })
      .sort((a, b) => {
        // Sorting logic
        if (a.role === 'owner' && b.role !== 'owner') {
          return -1;
        }
        if (a.role !== 'owner' && b.role === 'owner') {
          return 1;
        }
        return 0;
      });
  })


export const getAllKanbanIdsAndNames = createSelector(
  [
    (state: CommonRootState, currentBoardId: string) =>
      kanbanSelectByQuery(state.DBKanbanReducer, currentBoardId),
  ],
  (kanban: Kanban[]) => {
    return kanban.map((kanban, index) => [kanban._id, kanban.name]);
  }
);
export const getAllGanttIdsAndNames = createSelector(
  [
    (state: CommonRootState, currentBoardId: string) =>
      ganttSelectByQuery(state.DBGanttReducer, currentBoardId),
  ],
  (gantt: Gantt[]) => {
    return gantt.map((gantt, index) => [gantt._id, gantt.name]);
  }
);

export const getSelectedBoardCompleteUserListEqual = createSelector(
  [
    (state: CommonRootState) => getSelectedBoardCompleteUserList(state),
  ],
  (userList: AugmentedBlueticksBoardUser[]) => {
    return userList;
  }
)


export const getSelectedBoardCompleteUserList = createDeepEqualSelector(
  [
    (state: CommonRootState, boardId?: string) => getSelectedBoardUserList(state, boardId),
    (state: CommonRootState) => getMockPersonList(state),
  ],
  (
    boardUserList: BlueticksBoardUser[],
    mockPersonList
  ): AugmentedBlueticksBoardUser[] => {
    const userList: AugmentedBlueticksBoardUser[] = boardUserList.map(
      (boardUser) => ({
        ...boardUser,
        type: "user",
      })
    );
    return [...userList, ...mockPersonList];
  }
);

export const getActiveWorkspaceUserListNotInCurrentBoard = createSelector(
  [
    (state: CommonRootState) => getCurrentWorkspaceUserList(state),
    (state: CommonRootState) => getSelectedBoardUserList(state),
  ],
  (
    workspaceUserList: BlueticksWorkspaceUser[],
    boardUserList: BlueticksBoardUser[]
  ): BlueticksWorkspaceUser[] => {
    return workspaceUserList
      .filter((workspaceUser) => workspaceUser.status === "active")
      .filter(
        (workspaceUser) =>
          !boardUserList.find(
            (boardUser) => workspaceUser.userId === boardUser.userId
          )
      );
  }
);

export const getActiveWorkSpaceUsers = createSelector(
  [(state: CommonRootState) => getCurrentWorkspaceUserList(state)],
  (workspaceUserList: BlueticksWorkspaceUser[]): BlueticksWorkspaceUser[] => {
    return workspaceUserList.filter(
      (workspaceUser) => workspaceUser.status === "active"
    );
  }
);

// write a selector that returns workspace members
export const isCurrentUserMemberOrOwner = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => selectUserObject(state),
  (state: CommonRootState) => getCurrentWorkspaceUserList(state),
  (
    state: CommonRootState,
    currentUser: User | undefined,
    workspaceUserList: BlueticksWorkspaceUser[]
  ) => {


    const workSpaceMembers = workspaceUserList.filter(
      (workspaceUser) => workspaceUser?.role === "member" || workspaceUser?.role === "owner"
    );

    //check if current user is inside workSpaceMembers return boolean value
    const isCurrentUserMember = workSpaceMembers.some((member) => member.userId === currentUser?._id);


    return isCurrentUserMember;
  }
);

export const getSelectedBoardName = createSelector(
  [(state: CommonRootState) => getCurrentBoard(state)],
  (board: Board | undefined): string => {
    return board?.name ?? "";
  }
);

export const getBoardNameById = (state: CommonRootState, boardId: string) => {
  const board = state.DBBoardsReducer.entities[boardId];
  return board ? `${board.titleIcon ?? ""}${board.name ?? ""}` : "";
};

export const isBoardExistforWaContact = createSelector(
  [(state: CommonRootState) => getCurrentBoard(state)],
  (board: Board | undefined): boolean => {
    return !!board;
  }
);

export const getSelectedBoardIsShareByLink = createSelector(
  [(state: CommonRootState) => getCurrentBoard(state)],
  (board: Board | undefined): boolean => {
    return board?.isShareByLink ?? false;
  }
);

export const getSelectedBoardKanbanId = createSelector(
  [(state: CommonRootState) => getCurrentBoard(state)],
  (board: Board | undefined): NullableId => {
    return board?.kanbanId ?? null;
  }
);
export const getSelectedBoardGanttId = createSelector(
  [(state: CommonRootState) => getCurrentBoard(state)],
  (board: Board | undefined): NullableId => {
    return board?.ganttId ?? null;
  }
);



// FE#243: order by a type-robust recency signal (normalized epoch-ms) rather
// than delegating to sift-sort's `{ updatedAt: -1 }` string comparator, which
// floated timestamp-less / Date-typed rows to the top instead of the genuinely
// most-recent boards. See board-recency.ts.
export const getRecentBoards = createSelector(
  (state: CommonRootState) => state.DBBoardsReducer,
  (state: CommonRootState) => state.BoardsReducer.boardsFilterName,
  (DBBoardsReducer, boardsFilterName) => {
    let boardsFilter: any = {
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    };

    if (boardsFilterName) {
      boardsFilter.name = new RegExp(boardsFilterName, "i");
    }

    const currentBoards = boardsSelectByQuery(DBBoardsReducer, boardsFilter);

    return sortBoardsByRecency(currentBoards);
  }
);



export const getActiveBoards = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    let boardsFilter: any = {
      $and: [
        {
          $or: [
            { archived: { $exists: false } },
            { archived: false }
          ]
        }
      ]
    };

    if (state.BoardsReducer.boardsFilterName) {
      boardsFilter.name = new RegExp(state.BoardsReducer.boardsFilterName, "i");
    }



    const currentBoards = boardsSelectByQuery(
      state.DBBoardsReducer,
      boardsFilter,
      [
        "_id",
        "name",
        "titleIcon",
        "owner",
        "updatedAt",
        "workspace"
      ],
      {
        updatedAt: -1,
      }
    );

    return currentBoards;
  }
);

export const getCurrentBoards = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    let boardsFilter: any = {
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    };

    if (state.BoardsReducer.boardsFilterName) {
      boardsFilter.name = new RegExp(state.BoardsReducer.boardsFilterName, "i");
    }

    const currentBoards = boardsSelectByQuery(
      state.DBBoardsReducer,
      boardsFilter,
      ["_id", "name", "order", "updatedAt"],
      {
        order: -1,
      }
    );

    return currentBoards;
  }
);

export const getAllBoards = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    let boardsFilter: any = {};

    const currentBoards = boardsSelectByQuery(
      state.DBBoardsReducer,
      boardsFilter,
      ["_id", "name", "order", "updatedAt"],
      {
        order: -1,
      }
    );

    return currentBoards;
  }
);

export const getFirstBoardId = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    let boardsFilter: any = {
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    };

    if (state.BoardsReducer.boardsFilterName) {
      boardsFilter.name = new RegExp(state.BoardsReducer.boardsFilterName, "i");
    }

    const [currentBoards] = boardsSelectByQuery(
      state.DBBoardsReducer,
      boardsFilter,
      ["_id", "name", "order", "updatedAt"],
      {
        order: -1,
      }
    );

    return currentBoards._id;
  }
);


export const selectPendingBoardUsers = createSelector(
  [(state: CommonRootState) => state.DBBoardUserReducer, (state: CommonRootState) => selectCurrentUser(state)],
  (DBBoardUserReducer, user) => {

    return boardUserSelectByQuery(DBBoardUserReducer, {
      userId: user?._id,
      workspaceId: user?.currentWorkspace,
      status: "pending",
      deleted: false,
    });
  }

);


export const isCurrentBoardOwnerSelector = createSelector(
  [
    (state: CommonRootState) => getCurrentBoard(state),
    (state: CommonRootState) => selectCurrentUser(state),
  ],
  (board: Board | undefined, user: UserInterface | undefined) => {
    return board?.owner && board?.owner === user?._id;
  }
);

export const isCurrentUserWorkspaceOwnerOrMember = createSelector(
  [
    (state: CommonRootState) => getCurrentWorkspaceUserList(state),
    (state: CommonRootState) => selectCurrentUser(state),
  ],
  (workspaceUserList: BlueticksWorkspaceUser[], user: UserInterface | undefined) => {
    const currentWorkspaceUser = workspaceUserList.find(
      (workspaceUser) => workspaceUser.userId === user?._id
    );
    return currentWorkspaceUser?.role === "owner" || currentWorkspaceUser?.role === "member";
  }
);

// export const getViewFiltersLength = createSelector(

//     [
//       (state: CommonRootState) => getSelectedBoardId(state),
//     ],
//     (boardId, DBViewFiltersReducer) => {
//       return viewFiltersSelectByQueryOnlyLength(DBViewFiltersReducer, {
//         viewId: boardId,
//         $or: [{ deleted: { $exists: false } }, { deleted: false }],
//       });
//     }
//   );

export const getViewFiltersLength = createSelector(
  [
    (state: CommonRootState) => state,
    getSelectedBoardId,
  ],
  (state: CommonRootState, boardId) => {
    return viewFiltersSelectByQueryOnlyLength(state.DBViewFiltersReducer, {
      viewId: boardId,
      $or: [{ deleted: { $exists: false } }, { deleted: false }],
    });
  }
);




export const isUserBoardOwner = createSelector(
  [(state: CommonRootState) => state, (state: CommonRootState, props) => props.boardId],
  (state: CommonRootState, boardId) => {
    const user = selectCurrentUser(state);
    const board = boardsSelectOneObjectByQuery(state.DBBoardsReducer, {
      _id: boardId,
    });
    return board?.owner && board?.owner === user?._id;
  }
);
export const isCurrentWorkspaceOwnerSelector = createSelector(
  [(state: CommonRootState) => state, (state: CommonRootState) => selectCurrentUser(state)],
  (state: CommonRootState, user: UserInterface | undefined) => {
    if (!user?.currentWorkspace) {
      return false;
    }

    const [currentWorkspaceUser] = workspaceUserSelectByQuery(
      state.DBWorkspaceUserReducer,
      {
        userId: user._id,
        workspaceId: user?.currentWorkspace,
      }
    );

    return currentWorkspaceUser?.role === "owner";
  }
);

export const selectUserListAndCurrentUser = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => selectUserObject(state),
  (state: CommonRootState) => getCurrentWorkspaceUserList(state),
  (
    state: CommonRootState,
    currentUser: User | undefined,
    workspaceUserList: BlueticksWorkspaceUser[]
  ) => {
    const userList = workspaceUserList ?? [];
    const userName = currentUser?.fullName?.split(" ");
    const currentUserObj = {
      email: currentUser?.email,
      profile: {
        email: currentUser?.email,
        firstName: (userName?.length ?? 0) > 0 ? userName?.[0] : "",
        lastName: (userName?.length ?? 0) > 1 ? userName?.[1] : "",
        profileImg: currentUser?.profileImg,
      },
      role: "member",
      status: "active",
      userId: currentUser?._id,
    };
    const mergedUserList = [...userList, currentUserObj];
    return mergedUserList.filter((person) => !!person.userId);
  }
);

export const userRoleCountSelector = createSelector(
  [
    (state: CommonRootState) => getCurrentWorkspaceUserList(state),
    (state: CommonRootState, roleType) => roleType,
  ],
  (workspaceUserList: BlueticksWorkspaceUser[], roleType) => {
    return (workspaceUserList ?? []).reduce(
      (guestCount, user) => guestCount + (user?.role === roleType ? 1 : 0),
      0
    );
  }
);

export const getFeatureListForTasksHeaderSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => getSelectedBoardId(state),
    (_, indicator) => indicator,
    (_, __, width) => width,
    (_, __, ___, tabsRef) => tabsRef,
  ],
  (state: CommonRootState, selectedBoardId, indicator, width, tabsRef) => {
    const offset = 250;
    let widthOfParent = width - offset;
    //console.log(`length of tabs: ${tabsRef?.current?.length}`)
    let numberOfVisibleFeatures = 0;
    for (let i = 0; i < tabsRef?.current?.length; i++) {
      if (widthOfParent - tabsRef?.current[i].offsetWidth > 0) {
        widthOfParent = widthOfParent - tabsRef?.current[i].offsetWidth;
        numberOfVisibleFeatures++;
      }
    }

    // console.log(`Width: ${width}, number of visible features: ${numberOfVisibleFeatures}`)

    const _kanbansList = kanbanSelectByQuery(
      state.DBKanbanReducer,
      {
        boardId: selectedBoardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      ["_id", "name"]
    );

    const _formsList = formSelectByQuery(
      state.DBFormReducer,
      {
        boardId: selectedBoardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      ["_id", "name"]
    );

    const _ganttsList = ganttSelectByQuery(
      state.DBGanttReducer,
      {
        boardId: selectedBoardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      ["_id", "name"]
    );

    const _workloadsList = workloadSelectByQuery(
      state.DBWorkloadReducer,
      {
        boardId: selectedBoardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      ["_id", "name"]
    );
    const _boardsViewList = boardViewSelectByQuery(
      state.DBBoardViewReducer,
      {
        boardId: selectedBoardId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
      },
      ["_id", "name"]
    );
    const featureList = [
      { _id: selectedBoardId, name: "Board", type: "board" },
      ..._kanbansList.map((obj) => ({ ...obj, type: "kanban" })),
      ..._formsList.map((obj) => ({ ...obj, type: "form" })),
      ..._ganttsList.map((obj) => ({ ...obj, type: "gantt" })),
      ..._workloadsList.map((obj) => ({ ...obj, type: "workload" })),
      ..._boardsViewList.map((obj) => ({ ...obj, type: "boardView" })),
    ];

    // const offset = 2
    // const numberOfFeatureToRemove = featureList.length - numberOfVisibleFeatures - offset

    return indicator === -1
      ? featureList
      : featureList.slice(0, numberOfVisibleFeatures);
  }
);

export const doesEmailExistOnBoardUsers = createSelector(
  [(state: CommonRootState) => state, (_state: CommonRootState, email: string) => email],
  (state, email) => {
    const boardId = state.BoardsReducer?.selectedBoardId;
    const foundUser = boardUserSelectOneObjectByQuery(
      state.DBBoardUserReducer,
      {
        boardId,
        email: email.toLowerCase(),
      }
    );
    return !!foundUser;
  }
);

export const getRemainingFeatureForMoreButton = createSelector(
  [
    (state: CommonRootState, searchQuery) => state,
    (state: CommonRootState, searchQuery) => searchQuery,
    (_, ___, width) => width,
    (_, ___, ____, tabsRef) => tabsRef,
  ],
  (state: CommonRootState, searchQuery, width, tabsRef) => {
    const fullFeatureList = getFeatureListForTasksHeaderSelector(
      state,
      -1,
      width,
      tabsRef
    );

    const offset = 250;
    let widthOfParent = width - offset;
    //console.log(`length of tabs: ${tabsRef?.current?.length}`)
    let numberOfVisibleFeatures = 0;
    for (let i = 0; i < tabsRef?.current?.length; i++) {
      if (widthOfParent - tabsRef?.current[i].offsetWidth > 0) {
        widthOfParent = widthOfParent - tabsRef?.current[i].offsetWidth;
        numberOfVisibleFeatures++;
      }
    }

    // console.log(`Width: ${width}, number of visible features: ${numberOfVisibleFeatures}`)

    return searchQuery === ""
      ? fullFeatureList.slice(
        -(fullFeatureList.length - numberOfVisibleFeatures)
      )
      : fullFeatureList
        .slice(-(fullFeatureList.length - numberOfVisibleFeatures))
        .filter((feature) => {
          if (feature.name === "") {
            return feature.type
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase());
          } else {
            return feature.name
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase());
          }
        });
  }
);

export const cellPersonListSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState, personPickerValueList: PersonPickerValue[]) =>
      personPickerValueList,
    (
      state: CommonRootState,
      personPickerValueList: PersonPickerValue[],
      boardId?: string
    ) => boardId ?? getCurrentBoardId(state),
  ],
  (
    state,
    personPickerValueList: PersonPickerValue[],
    boardId: string | undefined
  ): AugmentedBlueticksBoardUser[] => {
    if (!Array.isArray(personPickerValueList) || !personPickerValueList) {
      return [];
    }
    if (!boardId) return [];
    const boardUserList: AugmentedBlueticksBoardUser[] = boardUserSelectByQuery(
      state.DBBoardUserReducer,
      {
        boardId,
        _id: {
          $in: (personPickerValueList ?? [])
            .filter((value) => value?.type === "user")
            .map((value) => value?.id),
        },
      }
    ).map((workspaceUser) => ({
      ...workspaceUser,
      type: "user",
    }));
    const _optionList = taskColumnOptionSelectByQuery(
      state.DBTaskColumnOptionReducer,
      {
        boardId,
        _id: {
          $in: (personPickerValueList ?? [])
            .filter((value) => value?.type === "option" || value?.type === "user")
            .map((value) => value?.id),
        },
      }
    );
    const optionList = _optionList.map((option) => {
      const [firstName, lastName] = option?.label?.split(" ");
      const workspaceUser: AugmentedBlueticksBoardUser = {
        _id: option?._id,
        status: "active",
        profile: {
          firstName,
          lastName,
          profileImg: option?.imageUrl,
        } as User,
        type: "option",
      };
      return workspaceUser;
    });

    return [...boardUserList, ...optionList];
  }
);

export const isBoardPanelDrawerOpen = createSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState) => {
    //const user = selectUser(state);
    //return user?.isBoardsPanelOpen ?? true;
    return true
  }
);


export const getBoardsPaginationStatus = createSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState) => {
    const boards = BoardsSelectPaginationStatus(state.DBBoardsReducer, {
    })

    return boards
  }
);


// export const getBoardNameById = createSelector(
//   (state: CommonRootState, boardId: string) => {
//     const boards = boardsSelectByQuery(state.DBBoardsReducer, {
//       _id: boardId,
//     });
//     return boards[0];
//   },
//   (board): string | undefined => {
//     return board?.name;
//   }
// );
export const getBulkListsPaginationStatus = createSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState) => {
    const bulkLists = BulkListSelectPaginationStatus(state.DBBulkListReducer, {
    });

    return bulkLists
  }
);

export const getDrawerWidth = createDeepEqualSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState) => {
    const board = getCurrentBoard(state)
    const width = board?.drawerWidth ? board.drawerWidth : 500;

    return width
  }
);
// Selector that returns true if the boardId is for a board with type 'audience'
export const doesGroupBelongToAudienceBoard = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState, groupId: string) => groupId,
  ],
  (state: CommonRootState, groupId: string) => {
    let boardId = taskGroupsSelectOneFieldByQuery(state.DBTaskgroupsReducer, { _id: groupId }, 'boardId');
    return boardId ? boardsSelectOneObjectByQuery(state.DBBoardsReducer, { _id: boardId })?.type === "audience" : false;
  }
);

// Selector that returns true if the taskId belongs to an audience board
export const doesTaskBelongToAudienceBoard = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState, taskId: string) => taskId,
  ],
  (state: CommonRootState, taskId: string) => {
    let boardId = tasksSelectOneFieldById(state.DBTasksReducer, taskId, 'boardId');
    return boardId ? boardsSelectOneObjectByQuery(state.DBBoardsReducer, { _id: boardId })?.type === "audience" : false;
  }
);
